import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { useToast } from '../context/ToastContext';
import ReviewModal from '../components/ReviewModal';
import ReportOrderIssueModal from '../components/ReportOrderIssueModal';
import OrderTracking from '../components/OrderTracking';
import DeliveryOrderSummary from '../components/DeliveryOrderSummary';
import OrderDineInCode from '../components/OrderDineInCode';
import { useLivePoll } from '../hooks/useLivePoll';
import OrderChargesCard, { orderChargesReceiptLines } from '../components/OrderChargesCard';
import jsPDF from 'jspdf';

interface OrderItem {
  menu_item_id: string;
  quantity: number;
  name?: string;
  price?: number;
  note?: string;
}

interface Order {
  id: string;
  order_number?: string;
  cafe_id: string;
  cafe_name: string;
  cafe_address: string;
  items: OrderItem[];
  total_amount: number;
  merchandise_subtotal_jod?: number | null;
  promotion_discount?: number | null;
  points_discount_jod?: number | null;
  wallet_used?: number | null;
  jordan_tax_jod?: number | null;
  service_fee_jod?: number | null;
  small_order_fee_jod?: number | null;
  delivery_fee_jod?: number | null;
  fees_jod?: number | null;
  points_earned: number;
  points_used: number;
  student_discount?: number;
  status: string;
  name?: string;
  payment_method?: string;
  created_at: string;
  fulfillment_type?: string | null;
  table_number?: number | null;
  dine_in_qr_token?: string | null;
  delivery_address?: string | null;
  delivery_distance_km?: number | null;
  delivery_duration_min?: number | null;
}

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [hasReview, setHasReview] = useState(false);
  const [issueReported, setIssueReported] = useState(false);
  const [myReview, setMyReview] = useState<{
    staff_reply?: string | null;
    staff_reply_at?: string | null;
  } | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isClick, setIsClick] = useState(true);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const fastReadRequest = { timeout: 15000, __skipWakeRetry: true } as any;

  useEffect(() => {
    if (id) {
      fetchOrder();
      checkReviewStatus();
      checkIssueStatus();
    }
  }, [id]);

  const checkIssueStatus = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`/api/orders/${id}/issue-status`, fastReadRequest);
      setIssueReported(!!res.data.issue);
    } catch {
      setIssueReported(false);
    }
  };

  const checkReviewStatus = async () => {
    try {
      const response = await axios.get<{
        reviewed: boolean;
        review?: { staff_reply?: string | null; staff_reply_at?: string | null };
      }>(`/api/reviews/order/${id}`, fastReadRequest);
      setHasReview(!!response.data.reviewed);
      setMyReview(response.data.reviewed && response.data.review ? response.data.review : null);
    } catch (error) {
      setHasReview(false);
      setMyReview(null);
    }
  };

  const fetchOrder = async (quiet = false) => {
    try {
      const response = await axios.get(`/api/orders/${id}`, fastReadRequest);
      setOrder(response.data);
    } catch (error: any) {
      if (quiet) return;
      console.error('Error fetching order:', error);
      showToast('Failed to load order details', 'error');
      navigate('/orders');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const fetchOrderRef = useRef(fetchOrder);
  fetchOrderRef.current = fetchOrder;
  useLivePoll(() => {
    void fetchOrderRef.current(true);
  }, 8000);

  const handleShare = async () => {
    if (!order) return;

    const receiptText = `Order Receipt\n\n` +
      `Order #: ${order.order_number || order.id.substring(0, 8)}\n` +
      `Cafe: ${order.cafe_name}\n` +
      `Address: ${order.cafe_address}\n` +
      `Date: ${new Date(order.created_at).toLocaleString()}\n\n` +
      `Items:\n${order.items.map(item => `  ${item.quantity}x ${item.name || 'Item'}\n`).join('')}\n` +
      `${orderChargesReceiptLines(order, order.items).join('\n')}\n` +
      `Payment: ${order.payment_method || 'N/A'}\n` +
      `Status: ${order.status}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Order ${order.order_number || order.id.substring(0, 8)}`,
          text: receiptText
        });
      } catch (err) {
        // User cancelled or error
        copyToClipboard(receiptText);
      }
    } else {
      copyToClipboard(receiptText);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Receipt copied to clipboard!', 'success');
    }).catch(() => {
      showToast('Failed to copy receipt', 'error');
    });
  };

  const generatePDF = () => {
    if (!order) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // Helper function to add text with word wrap
    const addText = (text: string, fontSize: number, isBold: boolean = false, align: 'left' | 'center' | 'right' = 'left') => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
      lines.forEach((line: string) => {
        if (yPos > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, align === 'center' ? pageWidth / 2 : align === 'right' ? pageWidth - margin : margin, yPos, { align });
        yPos += fontSize * 0.5 + 2;
      });
    };

    // Header
    addText('☕ QahwaHub', 20, true, 'center');
    yPos += 5;
    addText('Order Receipt', 16, true, 'center');
    yPos += 10;

    // Order Number
    if (order.order_number) {
      addText(`Order #: ${order.order_number}`, 12, true);
      yPos += 5;
    }

    // Date
    addText(`Date: ${new Date(order.created_at).toLocaleString()}`, 10);
    yPos += 8;

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Cafe Info
    addText(order.cafe_name, 14, true);
    yPos += 5;
    addText(`📍 ${order.cafe_address}`, 10);
    yPos += 8;

    // Order Name (if exists)
    if (order.name) {
      addText(`📝 ${order.name}`, 10);
      yPos += 8;
    }

    // Divider
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Items
    addText('Items:', 12, true);
    yPos += 5;
    
    order.items.forEach((item) => {
      const itemName = item.name || `Item #${item.menu_item_id.substring(0, 8)}`;
      const quantity = item.quantity;
      const price = item.price || 0;
      const itemTotal = price * quantity;

      addText(`${quantity}x ${itemName}`, 10);
      if (item.note) {
        addText(`   Note: ${item.note}`, 9);
      }
      if (price > 0) {
        const priceText = `${formatCurrency(price)} each = ${formatCurrency(itemTotal)}`;
        doc.setFont('helvetica', 'normal');
        doc.text(priceText, pageWidth - margin, yPos - 5, { align: 'right' });
      }
      yPos += 6;
    });

    yPos += 5;
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Summary
    addText('Charges:', 12, true);
    yPos += 3;
    orderChargesReceiptLines(order, order.items).forEach((line) => {
      addText(line, line.startsWith('Total:') ? 12 : 10, line.startsWith('Total:'));
      yPos += 2;
    });
    if (order.points_used > 0) {
      addText(`Points Used: -${order.points_used} points`, 10);
      yPos += 5;
    }

    // Payment Method
    if (order.payment_method) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      addText(`Payment Method: ${order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)}`, 10);
      yPos += 5;
    }

    // Points Earned
    if (order.status !== 'cancelled' && order.points_earned > 0) {
      addText(`Points Earned: +${order.points_earned}`, 10);
      yPos += 5;
    }

    // Status
    yPos += 5;
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    addText(`Status: ${order.status.toUpperCase()}`, 10, true);
    yPos += 10;

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    addText('Thank you for your order!', 8, false, 'center');
    yPos += 3;
    addText('Visit us again soon ☕', 8, false, 'center');

    // Generate filename
    const orderNum = order.order_number || order.id.substring(0, 8);
    const filename = `Order-${orderNum}-${new Date(order.created_at).toISOString().split('T')[0]}.pdf`;

    // Save PDF
    doc.save(filename);
    showToast('PDF receipt downloaded!', 'success');
  };

  const handlePrint = () => {
    generatePDF();
  };

  // Drag handlers for movable bill - optimized for fast dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't drag if clicking on buttons or interactive elements
    if ((e.target as HTMLElement).closest('button') || 
        (e.target as HTMLElement).closest('a') ||
        (e.target as HTMLElement).closest('input') ||
        (e.target as HTMLElement).closest('textarea')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true); // Start dragging immediately
    setIsClick(false);
    setStartPos({ x: e.clientX, y: e.clientY });
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - 400; // Approximate card width
      const maxY = window.innerHeight - 200; // Approximate card height
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsClick(true);
      setDragStart({ x: 0, y: 0 });
      setStartPos({ x: 0, y: 0 });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: false });
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Touch events for mobile - optimized for fast dragging
  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true); // Start dragging immediately
    setIsClick(false);
    const touch = e.touches[0];
    setStartPos({ x: touch.clientX, y: touch.clientY });
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragStart({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    const maxX = window.innerWidth - 400;
    const maxY = window.innerHeight - 200;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsClick(true);
    setDragStart({ x: 0, y: 0 });
    setStartPos({ x: 0, y: 0 });
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading order details...</div>;
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 mb-4">Order not found</p>
        <Link to="/orders" className="text-amber-700 hover:text-amber-800 font-medium">
          Back to Orders
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <Link to="/orders" className="text-amber-700 hover:text-amber-800 font-medium">
          ← Back to Orders
        </Link>
        <div className="flex gap-2">
          <button
            onClick={handleShare}
            className="bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 active:bg-amber-900 transition text-sm relative z-10"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            type="button"
          >
            📤 Share
          </button>
          <button
            onClick={handlePrint}
            className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800 active:bg-gray-900 transition text-sm relative z-10"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            type="button"
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div 
        className="bg-white rounded-lg shadow-md p-6 print:shadow-none relative"
        style={{
          transform: position.x !== 0 || position.y !== 0 ? `translate(${position.x}px, ${position.y}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
        }}
      >
        {/* Draggable Header - entire bill can be dragged */}
        <div 
          className="border-b pb-4 mb-4 cursor-move select-none"
          style={{ 
            userSelect: 'none', 
            WebkitUserSelect: 'none',
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-lg">⋮⋮</span>
              <h1 className="text-2xl font-bold text-amber-800">Order Receipt</h1>
            </div>
            <span className={`px-3 py-1 rounded text-sm font-semibold ${
              order.status === 'completed' ? 'bg-green-100 text-green-800' :
              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {order.status}
            </span>
          </div>
          {order.order_number && (
            <p className="text-gray-600 font-medium">Order #: {order.order_number}</p>
          )}
          <OrderDineInCode order={order} />
        </div>

        {/* Cafe Info */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-amber-800 mb-2">{order.cafe_name}</h2>
          <p className="text-gray-600">📍 {order.cafe_address}</p>
          <DeliveryOrderSummary order={order} className="text-sm text-amber-800 mt-2" />
          <p className="text-sm text-gray-600 mt-2">
            Questions about this order? Email{' '}
            <a className="text-amber-800 font-semibold" href="mailto:support@qahwahub.com">
              support@qahwahub.com
            </a>
          </p>
        </div>

        {/* Order Tracking */}
        {order.status !== 'cancelled' && (
          <div className="mb-6">
            <OrderTracking status={order.status} orderNumber={order.order_number} />
          </div>
        )}

        {/* Order Name */}
        {order.name && (
          <div className="mb-4 p-3 bg-amber-50 rounded">
            <p className="text-amber-800 font-medium">📝 {order.name}</p>
          </div>
        )}

        {/* Items */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">Items:</h3>
          <div className="space-y-3">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-start pb-2 border-b">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.quantity}x</span>
                    <span className="font-medium">{item.name || `Item #${item.menu_item_id.substring(0, 8)}`}</span>
                  </div>
                  {item.note && (
                    <p className="text-sm text-gray-500 mt-1 italic">Note: {item.note}</p>
                  )}
                  {item.price && (
                    <p className="text-sm text-gray-600 mt-1">
                      {formatCurrency(item.price)} each
                    </p>
                  )}
                </div>
                {item.price && (
                  <span className="font-semibold">{formatCurrency(item.price * item.quantity)}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="border-t pt-4 space-y-3">
          <OrderChargesCard order={order} items={order.items} />
          {order.points_used > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">🎁 Points Used:</span>
              <span className="text-amber-600">-{order.points_used} points</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold pt-2 border-t">
            <span>Total:</span>
            <span className="text-amber-700">{formatCurrency(order.total_amount)}</span>
          </div>
        </div>

        {/* Payment & Points */}
        <div className="mt-6 pt-4 border-t space-y-2">
          {order.payment_method && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">💳 Payment Method:</span>
              <span className="font-medium">{order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)}</span>
            </div>
          )}
          {order.status !== 'cancelled' && order.points_earned > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">✨ Points Earned:</span>
              <span className="text-green-600 font-medium">+{order.points_earned}</span>
            </div>
          )}
        </div>

        {/* Review Button - only show for completed orders */}
        {order.status === 'completed' && (
          <div className="mt-6 pt-4 border-t space-y-3">
            {issueReported ? (
              <button
                type="button"
                onClick={() => setShowIssueModal(true)}
                className="w-full text-sm text-amber-900 font-semibold text-center py-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100"
                style={{ touchAction: 'manipulation' }}
              >
                View report status
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowIssueModal(true)}
                className="w-full border border-gray-400 text-gray-900 px-4 py-3 rounded-lg hover:bg-gray-50 transition font-medium"
                style={{ touchAction: 'manipulation' }}
              >
                Report an issue
              </button>
            )}
            {hasReview ? (
              <div className="bg-green-50 dark:bg-green-900 p-4 rounded text-center space-y-3">
                <p className="text-green-700 dark:text-green-200 font-medium">✓ You've already reviewed this order</p>
                {myReview?.staff_reply && (
                  <div className="text-left rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800 p-3">
                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
                      Reply from {order.cafe_name}
                      {myReview.staff_reply_at && (
                        <span className="font-normal normal-case text-amber-800 dark:text-amber-300 ml-1">
                          · {new Date(myReview.staff_reply_at).toLocaleString()}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-100 mt-2 whitespace-pre-wrap">
                      {myReview.staff_reply}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowReviewModal(true)}
                className="w-full bg-amber-700 text-white px-4 py-3 rounded hover:bg-amber-800 transition font-medium"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)' }}
              >
                ⭐ Rate Your Experience
              </button>
            )}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {order && (
        <>
          <ReportOrderIssueModal
            order={order}
            isOpen={showIssueModal}
            onClose={() => setShowIssueModal(false)}
            onSubmitted={() => {
              setIssueReported(true);
              checkIssueStatus();
            }}
          />
          <ReviewModal
            orderId={order.id}
            cafeName={order.cafe_name}
            orderItems={order.items}
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onReviewSubmitted={() => {
              setHasReview(true);
              checkReviewStatus();
            }}
          />
        </>
      )}
    </div>
  );
}
